<template>
  <main class="ap-document">
    <div v-if="loading" class="ap-state">Loading invoice…</div>
    <div v-else-if="errorMessage" class="ap-state ap-error">
      <h1>Invoice unavailable</h1>
      <p>{{ errorMessage }}</p>
    </div>

    <template v-else-if="details">
      <header class="ap-header">
        <h1>INVOICE</h1>
        <button type="button" class="btn btn-outline-secondary print-button" @click="printInvoice">Print</button>
      </header>

      <section>
        <h2>AP #{{ details.invoice.number }}</h2>
        <div class="ap-parties">
          <div>
            <strong>Bill to:</strong><br>
            Language Buró OÜ<br>
            Sepapaja tn 6<br>
            Tallinn 15551<br>
            Estonia<br>
            VAT: EE101964197
          </div>
          <div>
            <strong>Vendor:</strong><br>
            {{ details.vendor.name }}<br>
            <span class="preserve-lines">{{ details.vendor.address }}</span><br>
            {{ details.vendor.taxId }}
          </div>
          <div>
            <strong>Date:</strong> {{ formatLongDate(details.invoice.date) }}<br>
            <template v-if="details.vendor.taxNote">
              <strong>Notes:</strong> {{ details.vendor.taxNote }}
            </template>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table ap-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Order Number</th>
                <th>Job ID</th>
                <th>Order completed date</th>
                <th>Unit</th>
                <th class="text-end">Units</th>
                <th class="text-end">Unit Price</th>
                <th class="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in details.jobs" :key="job.jobId">
                <td>{{ job.service }}<small v-if="job.title" class="d-block text-muted">{{ job.title }}</small></td>
                <td>{{ job.orderNumber }}</td>
                <td>{{ job.jobId }}</td>
                <td>{{ formatShortDate(job.completedOn) }}</td>
                <td>{{ job.unit }}</td>
                <td class="text-end">{{ formatNumber(job.units, 0, 4) }}</td>
                <td class="text-end text-nowrap">{{ job.currency }} {{ formatNumber(job.unitPrice, 2, 4) }}</td>
                <td class="text-end text-nowrap">{{ job.currency }} {{ formatNumber(job.total, 2, 2) }}</td>
              </tr>
              <tr class="ap-total">
                <td colspan="7" class="text-end">Total</td>
                <td class="text-end text-nowrap">
                  {{ details.invoice.currency }} {{ formatNumber(details.invoice.total, 2, 2) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <footer>&copy;{{ currentYear }} Language Buró OÜ</footer>
    </template>
  </main>
</template>

<script>
import { fetchPublicAccountsPayable } from '@/services/accountsPayable';

export default {
  name: 'AccountsPayableInvoice',

  data() {
    return {
      details: null,
      loading: true,
      errorMessage: '',
      originalTitle: document.title,
      currentYear: new Date().getFullYear(),
    };
  },

  methods: {
    async loadInvoice() {
      try {
        this.details = await fetchPublicAccountsPayable(
          this.$route.params.id,
          this.$route.query.token,
        );
        document.title = `AP Invoice ${this.details.invoice.number} - ${this.details.vendor.name}`;
      } catch (error) {
        this.errorMessage = error.response?.status === 404
          ? 'This link is invalid or the invoice no longer exists.'
          : 'The invoice could not be loaded. Please try again later.';
      } finally {
        this.loading = false;
      }
    },

    printInvoice() {
      window.print();
    },

    asDate(value) {
      return value ? new Date(`${value}T00:00:00Z`) : null;
    },

    formatLongDate(value) {
      const date = this.asDate(value);
      return date ? new Intl.DateTimeFormat('en-US', {
        month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
      }).format(date) : '';
    },

    formatShortDate(value) {
      const date = this.asDate(value);
      return date ? new Intl.DateTimeFormat('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
      }).format(date) : '';
    },

    formatNumber(value, minimumFractionDigits, maximumFractionDigits) {
      if (value === null || value === undefined || value === '') return '';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
      }).format(value);
    },
  },

  created() {
    this.loadInvoice();
  },

  beforeUnmount() {
    document.title = this.originalTitle;
  },
};
</script>

<style scoped>
.ap-document {
  max-width: 1180px;
  margin: 0 auto;
  padding: 40px 32px;
  color: #252525;
  background: #fff;
}

.ap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 3px solid #252525;
  margin-bottom: 36px;
  padding-bottom: 16px;
}

.ap-header h1,
.ap-document h2 {
  margin: 0;
  font-weight: 700;
}

.ap-document h2 {
  margin-bottom: 28px;
}

.ap-parties {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 36px;
  margin-bottom: 42px;
  line-height: 1.55;
}

.preserve-lines {
  white-space: pre-line;
}

.ap-table {
  font-size: 0.9rem;
}

.ap-table thead th {
  border-bottom: 2px solid #252525;
  white-space: nowrap;
}

.ap-total td {
  border-top: 2px solid #252525;
  font-weight: 700;
}

.ap-document footer {
  border-top: 1px solid #bbb;
  color: #777;
  font-size: 0.8rem;
  margin-top: 50px;
  padding-top: 14px;
}

.ap-state {
  padding: 80px 20px;
  text-align: center;
}

.ap-error {
  color: #842029;
}

@media (max-width: 767px) {
  .ap-document {
    padding: 24px 16px;
  }

  .ap-parties {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media print {
  .ap-document {
    max-width: none;
    padding: 0;
  }

  .print-button {
    display: none;
  }

  .table-responsive {
    overflow: visible;
  }

  .ap-table {
    font-size: 9pt;
  }
}
</style>
